import type { EventHelperRole } from "@/api/club/ClubApi";
import {
  useDeleteEventHelperRole,
  useJoinHelperRole,
  useLeaveHelperRole,
  useUpsertEventHelperRole,
} from "@/api/club/useClub";
import { useTheme } from "@/theme/ThemeProvider";
import { useStyles } from "@/theme/styles";
import { AppButton } from "@/ui/components/AppButton";
import { TextField } from "@/ui/components/FormField";
import React, { useState } from "react";
import { Text, View } from "react-native";

export function EventHelpersPanel({
  itemId,
  organizationId,
  roles,
  canManage,
  disabled,
}: {
  itemId: string;
  organizationId?: string;
  roles: EventHelperRole[];
  canManage?: boolean;
  disabled?: boolean;
}) {
  const { theme } = useTheme();
  const s = useStyles();
  const [name, setName] = useState("");
  const [desired, setDesired] = useState("");

  const upsert = useUpsertEventHelperRole(organizationId);
  const del = useDeleteEventHelperRole(itemId, organizationId);
  const join = useJoinHelperRole(itemId, organizationId);
  const leave = useLeaveHelperRole(itemId, organizationId);

  return (
    <View style={{ gap: theme.space.md }}>
      <Text style={s.textH3}>Helferrollen</Text>

      {roles.length === 0 ? (
        <Text style={[s.textBody, { color: theme.color.muted }]}>
          Noch keine Helferrollen.
        </Text>
      ) : null}

      {roles.map((role) => {
        const assigned = role.assignments?.length ?? 0;
        const desiredCount = role.desired_count;
        const label =
          desiredCount != null
            ? `${role.name} ${assigned} / ${desiredCount}`
            : `${role.name} ${assigned}`;

        return (
          <View
            key={role.id}
            style={{
              borderWidth: 1,
              borderColor: theme.color.border,
              borderRadius: theme.radius.md,
              padding: theme.space.md,
              backgroundColor: theme.color.surface,
              gap: theme.space.sm,
            }}
          >
            <Text style={[s.textBody, { fontWeight: "600" }]}>{label}</Text>
            {(role.assignments ?? []).map((a) => (
              <Text
                key={a.profile_id}
                style={[s.textTiny, { color: theme.color.muted }]}
              >
                · {a.display_name ?? a.profile_id}
              </Text>
            ))}
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: theme.space.sm }}>
              {role.my_assigned ? (
                <AppButton
                  variant="outline"
                  disabled={disabled || leave.isPending}
                  onPress={() => leave.mutate(role.id)}
                >
                  Austragen
                </AppButton>
              ) : (
                <AppButton
                  variant="secondary"
                  disabled={disabled || join.isPending}
                  onPress={() => join.mutate(role.id)}
                >
                  Eintragen
                </AppButton>
              )}
              {canManage ? (
                <AppButton
                  variant="ghost"
                  disabled={disabled || del.isPending}
                  onPress={() => del.mutate(role.id)}
                >
                  Rolle löschen
                </AppButton>
              ) : null}
            </View>
          </View>
        );
      })}

      {canManage && !disabled ? (
        <View style={{ gap: theme.space.sm }}>
          <Text style={s.textTiny}>Neue Rolle</Text>
          <TextField
            label="Bezeichnung"
            value={name}
            onChangeText={setName}
            placeholder="z. B. Standdienst"
          />
          <TextField
            label="Gewünschte Anzahl (optional)"
            value={desired}
            onChangeText={setDesired}
            keyboardType="number-pad"
          />
          <AppButton
            loading={upsert.isPending}
            disabled={!name.trim()}
            onPress={async () => {
              const n = desired.trim() ? parseInt(desired, 10) : null;
              await upsert.mutateAsync({
                eventItemId: itemId,
                name: name.trim(),
                desiredCount: n && n > 0 ? n : null,
              });
              setName("");
              setDesired("");
            }}
          >
            Rolle anlegen
          </AppButton>
        </View>
      ) : null}
    </View>
  );
}
